import { Link } from 'react-router-dom';
import { useI18n } from '@v2/providers/I18nProvider';
import { cn } from '@v2/utils/cn';
import ProductThumbnail from './ProductThumbnail';
import type { ProductListItem } from './types';

const formatBRL = (n: number | null) =>
    n == null ? '—' : n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const TYPE_TONE: Record<string, string> = {
    primary: 'bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-300',
    info: 'bg-sky-100 text-sky-800 dark:bg-sky-950/50 dark:text-sky-300',
    success: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300',
    warning: 'bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-300',
    danger: 'bg-rose-100 text-rose-800 dark:bg-rose-950/50 dark:text-rose-300',
};

const MAX_DOTS = 6;

function StockCell({ value, label, tone }: { value: number; label: string; tone: 'emerald' | 'amber' | 'sky' }) {
    const zero = value <= 0;
    return (
        <div className="flex min-w-0 flex-col items-center px-1 py-2">
            <span
                className={cn(
                    'text-base font-bold leading-none tabular-nums',
                    zero && 'text-gray-300 dark:text-gray-600',
                    !zero && tone === 'emerald' && 'text-emerald-600 dark:text-emerald-400',
                    !zero && tone === 'amber' && 'text-amber-600 dark:text-amber-400',
                    !zero && tone === 'sky' && 'text-sky-600 dark:text-sky-400',
                )}
            >
                {value}
            </span>
            <span className="mt-1 max-w-full truncate text-[10px] font-medium text-gray-400">{label}</span>
        </div>
    );
}

type Props = { product: ProductListItem };

export default function ProductGridCard({ product }: Props) {
    const { t } = useI18n();
    const stockCd = product.stock_cd ?? product.stock_quantity ?? 0;
    const stockTransit = product.stock_transit ?? 0;
    // stock_cd já é o disponível; reservado vem à parte — sem isso o vendedor achava que tinha mais.
    const stockReserved = product.stock_reserved ?? 0;
    const parts = product.color_swatches?.parts ?? [];
    // Estrutura é a cor que o cliente escolhe primeiro; as outras partes ficam no tooltip/detalhe.
    const frame = parts.find((p) => p.part === 'frame') ?? parts[0];
    const meta = [product.category, product.series].filter(Boolean).join(' · ');

    return (
        <Link
            to={`/products/${product.id}`}
            className="group flex flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm transition hover:-translate-y-0.5 hover:border-gray-300 hover:shadow-md dark:border-gray-700 dark:bg-gray-800 dark:hover:border-gray-600"
        >
            <div className="relative shrink-0 bg-white dark:bg-gray-900">
                <ProductThumbnail src={product.image_url} alt={product.name} size="grid" />
                {(product.suspended || stockCd <= 0) && (
                    <span className="pointer-events-none absolute left-2 top-2 z-10 flex flex-col items-start gap-1">
                        {product.suspended && (
                            <span className="rounded-md bg-gray-900/80 px-2 py-0.5 text-[11px] font-semibold text-white backdrop-blur-sm">
                                {t('v2.products.suspended')}
                            </span>
                        )}
                        {!product.suspended && stockCd <= 0 && stockTransit <= 0 && (
                            <span className="rounded-md bg-rose-600/90 px-2 py-0.5 text-[11px] font-semibold text-white backdrop-blur-sm">
                                {t('v2.products.card_no_stock')}
                            </span>
                        )}
                    </span>
                )}
            </div>

            <div className="grid grid-cols-3 divide-x divide-gray-100 border-y border-gray-100 bg-gray-50/60 dark:divide-gray-700 dark:border-gray-700 dark:bg-gray-900/40">
                <StockCell value={stockCd} label={t('v2.products.card_available')} tone="emerald" />
                <StockCell value={stockReserved} label={t('v2.products.card_reserved')} tone="amber" />
                <StockCell value={stockTransit} label={t('v2.products.card_transit')} tone="sky" />
            </div>

            <div className="flex flex-1 flex-col gap-2 p-4">
                <div>
                    {meta && <p className="mb-0.5 truncate text-[10px] font-semibold uppercase tracking-wide text-gray-400">{meta}</p>}
                    <p className="line-clamp-2 text-sm font-semibold text-gray-900 group-hover:text-primary dark:text-gray-100">{product.name}</p>
                    <p className="mt-0.5 font-mono text-[11px] text-gray-500 dark:text-gray-400">{product.code ?? '—'}</p>
                </div>

                <p className="text-lg font-bold tabular-nums text-gray-900 dark:text-gray-100">{formatBRL(product.unit_price)}</p>

                {frame ? (
                    <div className="flex min-w-0 items-center gap-1.5" title={frame.colors.map((c) => c.name).join(', ')}>
                        <span className="flex shrink-0 items-center -space-x-0.5">
                            {frame.colors.slice(0, MAX_DOTS).map((c) => (
                                <span
                                    key={c.name}
                                    className={cn(
                                        'size-3.5 rounded-full ring-2 ring-white dark:ring-gray-800',
                                        !c.hex && 'bg-[repeating-linear-gradient(45deg,#e5e7eb_0_2px,#fff_2px_4px)]',
                                    )}
                                    style={c.hex ? { backgroundColor: c.hex, boxShadow: 'inset 0 0 0 1px rgba(0,0,0,.12)' } : undefined}
                                />
                            ))}
                        </span>
                        {frame.colors.length > MAX_DOTS && (
                            <span className="shrink-0 text-[10px] font-semibold text-gray-500">+{frame.colors.length - MAX_DOTS}</span>
                        )}
                        <span className="ml-auto truncate text-[10px] text-gray-400">
                            {t('v2.products.colors_count', { count: product.color_swatches?.total ?? frame.colors.length })}
                        </span>
                    </div>
                ) : (
                    !!product.colors?.length && <p className="truncate text-[11px] text-gray-500">{product.colors.join(' · ')}</p>
                )}

                <div className="mt-auto flex min-w-0 items-center gap-2 pt-2">
                    {product.product_type_label && (
                        <span
                            className={cn(
                                'shrink-0 rounded-md px-1.5 py-0.5 text-[10px] font-semibold',
                                TYPE_TONE[product.product_type_color ?? ''] ?? 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
                            )}
                        >
                            {product.product_type_label}
                        </span>
                    )}
                </div>
            </div>
        </Link>
    );
}
